import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { ArrowLeft, BarChart3, Loader2 } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const COLORS = ["#2563eb", "#10b981", "#f59e0b"];

const CampaignComparePage = () => {
  const navigate = useNavigate();
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);

  // Fetch all campaigns
  const { data: campaigns, isLoading: loadingCampaigns } = useQuery({
    queryKey: ["campaigns-for-compare"],
    queryFn: async () => {
      const response = await api.get("/campaigns?includeArchived=true");
      return response.data;
    },
  });

  // Fetch comparison data when campaigns selected
  const { data: comparisonData, isLoading: loadingComparison } = useQuery({
    queryKey: ["campaign-comparison", selectedCampaigns],
    queryFn: async () => {
      if (selectedCampaigns.length < 2) return null;
      const response = await api.get(`/campaigns/compare?campaignIds=${selectedCampaigns.join(",")}`);
      return response.data;
    },
    enabled: selectedCampaigns.length >= 2,
  });

  const toggleCampaign = (campaignId) => {
    setSelectedCampaigns((prev) => {
      if (prev.includes(campaignId)) {
        return prev.filter((id) => id !== campaignId);
      }
      if (prev.length >= 3) {
        return prev; // Max 3 campaigns
      }
      return [...prev, campaignId];
    });
  };

  // Transform data for Recharts
  const chartData = comparisonData
    ? comparisonData[0]?.data?.map((item, index) => {
        const point = { date: item.date.slice(5) }; // MM-DD format
        comparisonData.forEach((campaign, campIndex) => {
          point[campaign.name] = campaign.data[index]?.openRate || 0;
        });
        return point;
      })
    : [];

  return (
    <div className="space-y-8" data-testid="campaign-compare-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/campaigns")}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold font-heading">Confronta Campagne</h1>
          <p className="text-muted-foreground">
            Seleziona 2-3 campagne per confrontare i tassi di apertura nel tempo
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Campaign Selection */}
        <Card className="bg-white border border-slate-100 lg:col-span-1">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Seleziona Campagne</CardTitle>
            <CardDescription>
              Seleziona da 2 a 3 campagne ({selectedCampaigns.length}/3)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingCampaigns ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12" />
                ))}
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {campaigns?.map((campaign, index) => (
                  <div
                    key={campaign.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                      selectedCampaigns.includes(campaign.id)
                        ? "border-primary bg-primary/5"
                        : "border-slate-100 hover:bg-slate-50"
                    }`}
                    onClick={() => toggleCampaign(campaign.id)}
                  >
                    <Checkbox
                      checked={selectedCampaigns.includes(campaign.id)}
                      onCheckedChange={() => toggleCampaign(campaign.id)}
                      disabled={
                        !selectedCampaigns.includes(campaign.id) &&
                        selectedCampaigns.length >= 3
                      }
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {selectedCampaigns.includes(campaign.id) && (
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{
                              backgroundColor:
                                COLORS[selectedCampaigns.indexOf(campaign.id)],
                            }}
                          />
                        )}
                        <p className="font-medium text-sm truncate">{campaign.name}</p>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {campaign.recipientCount} destinatari • {campaign.openRate}% aperture
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Comparison Chart */}
        <Card className="bg-white border border-slate-100 lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              Confronto Tassi di Apertura
            </CardTitle>
            <CardDescription>
              Andamento del tasso di apertura cumulativo negli ultimi 30 giorni
            </CardDescription>
          </CardHeader>
          <CardContent>
            {selectedCampaigns.length < 2 ? (
              <div className="h-80 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>Seleziona almeno 2 campagne per visualizzare il confronto</p>
                </div>
              </div>
            ) : loadingComparison ? (
              <div className="h-80 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fff",
                        border: "1px solid #e2e8f0",
                        borderRadius: "8px",
                      }}
                      formatter={(value) => [`${value}%`, "Tasso Apertura"]}
                    />
                    <Legend />
                    {comparisonData?.map((campaign, index) => (
                      <Line
                        key={campaign.id}
                        type="monotone"
                        dataKey={campaign.name}
                        stroke={COLORS[index]}
                        strokeWidth={2}
                        dot={{ fill: COLORS[index], strokeWidth: 2 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Summary Table */}
      {comparisonData && comparisonData.length >= 2 && (
        <Card className="bg-white border border-slate-100">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Riepilogo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Campagna
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Destinatari
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Aperture Uniche
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Tasso Apertura Finale
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((campaign, index) => {
                    const lastData = campaign.data[campaign.data.length - 1];
                    return (
                      <tr key={campaign.id} className="border-b border-slate-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: COLORS[index] }}
                            />
                            <span className="font-medium">{campaign.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right tabular-nums">
                          {campaign.recipientCount}
                        </td>
                        <td className="py-3 px-4 text-right tabular-nums">
                          {lastData?.uniqueOpens || 0}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="font-semibold">{lastData?.openRate || 0}%</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CampaignComparePage;
